import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Radio,
  Input,
  Button,
  Spin,
  Rate,
  Checkbox,
  Space,
  Slider,
  Row,
  Col,
  Modal,
} from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import SurveyApi from '../../Api/SurveyApi';
import { useAuth } from '../../Context/AuthContext';
import BackButton from '../ReusableComponents/BackButton';
const { Text, Title } = Typography;

const ApprovalsQuestions = () => {
  const { authState } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = useSelector((state) => state.auth?.user);

  const numericId = id ? Number(id) : null;
  const passedQuestions = location.state?.allQuestions || [];

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null); // 'Approved' | 'Rejected' | null
  const [surveyQuestions, setSurveyQuestions] = useState([]);

  // 1. Fetch Survey Data
  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const res = await SurveyApi.getById(numericId);
      if (res?.data?.success) {
        const surveyData = res.data.result?.rows?.[0] || res.data.result;
        if (surveyData) {
          setSurvey(surveyData);
          if (passedQuestions.length === 0 && surveyData.question) {
            const allQs = surveyData.question.flatMap(
              (cat) => cat.questions || [],
            );
            setSurveyQuestions(allQs);
          }
        }
      }
    } catch (err) {
      toastError('Connection Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
  }, [numericId]);

  useEffect(() => {
    if (passedQuestions.length > 0) {
      setSurveyQuestions(passedQuestions);
    }
  }, [passedQuestions]);

  const handleApprovalAction = async (status) => {
    if (!survey) return;

    try {
      setSubmittingAction(status);

      const payload = {
        survey_id: Number(survey.id),
        user_id: authState.id,
        approval_status: status,
      };

      console.log('Submitting Status:', payload);

      const res = await SurveyApi.SurveyApproved(payload);

      if (res.data?.success) {
        toastSuccess(`Survey ${status} successfully!`);
        navigate(-1);
      } else {
        toastError(res.data?.message || 'Action failed');
      }
    } catch (err) {
      toastError('Connection Error: ' + err.message);
    } finally {
      setSubmittingAction(null);
    }
  };

  // 3. Input Component Renderer (View-Only Mode)
  const renderInputType = (qItem) => {
    const likert = qItem.likert_scale || {};
   const options = Array.isArray(likert.options)
  ? likert.options
  : likert.options?.columns || [];
    const type = (likert.question_type || likert.type || 'text').toLowerCase();

    switch (type) {
      case 'matrix': {
        return null; // Handled by renderQuestions grouping
      }

      case 'checkbox':
        return (
          <Checkbox.Group disabled>
            <Space direction="vertical">
              {options.map((opt, i) => (
                <Checkbox key={i} value={opt.title}>
                  {opt.title}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      case 'radio':
        return (
          <Radio.Group disabled>
            <Space direction="vertical">
              {options.map((opt, i) => (
                <Radio key={i} value={opt.title}>
                  {opt.title}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'sliders':
      case 'slider': {
        const sliderMin = Number(options?.[0]?.min ?? 0);
        const sliderMax = Number(options?.[0]?.max ?? 100);
        return (
          <div style={{ padding: '0 20px 30px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, color: '#888', minWidth: 20 }}>
                {sliderMin}
              </span>
              <Slider
                min={sliderMin}
                max={sliderMax}
                disabled
                tooltip={{ formatter: (val) => `${val}` }}
                style={{ flex: 1 }}
                styles={{
                  track: { backgroundColor: '#134780' },
                  handle: { borderColor: '#134780', backgroundColor: '#fff' },
                }}
              />
              <span style={{ fontSize: 12, color: '#888', minWidth: 28 }}>
                {sliderMax}
              </span>
            </div>
          </div>
        );
      }

      case 'rating': {
        const starCount = Number(options?.[0]?.title) || 5;
        return <Rate disabled count={starCount} />;
      }

      default:
        return (
          <Input.TextArea rows={3} placeholder="User response area" disabled />
        );
    }
  };

  const renderMatrixTable = (group, startIdx, displayNum) => {
    const firstItem = group[0];
   const rawOptions = firstItem.likert_scale?.options;
const options = Array.isArray(rawOptions)
  ? rawOptions
  : rawOptions?.columns || [];
    const isRequired = group.some((q) => q.required || q.is_required);

    return (
      <div key={`matrix-${startIdx}`} style={{ marginBottom: 30 }}>
        <Text
          strong
          style={{
            fontSize: '15px',
            display: 'block',
            marginBottom: 15,
            color: '#444',
          }}
        >
          {displayNum}. {firstItem.title}{' '}
          {isRequired && <span style={{ color: 'red' }}>*</span>}
        </Text>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '250px' }}></th>
                {options.map((col, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '10px',
                      textAlign: 'center',
                      color: '#666',
                    }}
                  >
                    {typeof col === 'object' ? col.title : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.map((qItem, gIdx) => {
                return (
                  <tr key={gIdx} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '15px 10px', color: '#444' }}>
                      {qItem.likert_scale?.question || qItem.title}
                    </td>
                    {options.map((col, colIdx) => (
                      <td key={colIdx} style={{ textAlign: 'center' }}>
                        <Radio disabled />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMatrixTableNew = (qItem, startIdx, displayNum) => {
    const likert = qItem.likert_scale || {};
    // Support both old format (likert.rows + likert.options[]) and new format (likert.options.rows + likert.options.columns)
    const nestedOptions =
      likert.options && !Array.isArray(likert.options) ? likert.options : null;
    const columns = nestedOptions
      ? nestedOptions.columns || []
      : Array.isArray(likert.options)
        ? likert.options
        : [];
    const rows = nestedOptions ? nestedOptions.rows || [] : likert.rows || [];
    const isRequired = qItem.required || qItem.is_required;

    return (
      <div key={`matrix-${startIdx}`} style={{ marginBottom: 30 }}>
        <Text
          strong
          style={{
            fontSize: '15px',
            display: 'block',
            marginBottom: 15,
            color: '#444',
          }}
        >
          {displayNum}. {qItem.title}{' '}
          {isRequired && <span style={{ color: 'red' }}>*</span>}
        </Text>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '250px' }}></th>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '10px',
                      textAlign: 'center',
                      color: '#666',
                    }}
                  >
                    {typeof col === 'object' ? col.title : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((rowItem, rIdx) => {
                return (
                  <tr key={rIdx} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '15px 10px', color: '#444' }}>
                      {typeof rowItem === 'object' ? rowItem.title : rowItem}
                    </td>
                    {columns.map((col, colIdx) => (
                      <td key={colIdx} style={{ textAlign: 'center' }}>
                        <Radio disabled />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuestions = () => {
    const rendered = [];
    let displayIdx = 1;

    for (let i = 0; i < surveyQuestions.length; i++) {
      const qItem = surveyQuestions[i];
      const type = (
        qItem.likert_scale?.question_type ||
        qItem.likert_scale?.type ||
        ''
      ).toLowerCase();

      if (type === 'matrix') {
        const likert = qItem.likert_scale || {};
        const nestedOptions =
          likert.options && !Array.isArray(likert.options)
            ? likert.options
            : null;
        const rowsObj = nestedOptions
          ? nestedOptions.rows || []
          : likert.rows || [];
        if (rowsObj && rowsObj.length > 0) {
          rendered.push(renderMatrixTableNew(qItem, i, displayIdx));
          displayIdx++;
        } else {
          const group = [];
          const currentTitle = qItem.title;
          let j = i;

          while (
            j < surveyQuestions.length &&
            surveyQuestions[j].title === currentTitle &&
            (surveyQuestions[j].likert_scale?.question_type?.toLowerCase() ===
              'matrix' ||
              surveyQuestions[j].likert_scale?.type?.toLowerCase() === 'matrix')
          ) {
            group.push(surveyQuestions[j]);
            j++;
          }
          rendered.push(renderMatrixTable(group, i, displayIdx));
          displayIdx++;
          i = j - 1;
        }
      } else {
        rendered.push(
          <div key={i} style={{ marginBottom: 30 }}>
            <Text
              strong
              style={{
                fontSize: '15px',
                display: 'block',
                marginBottom: 20,
                color: '#444',
              }}
            >
              {displayIdx}. {qItem.title}{' '}
              {(qItem.required || qItem.is_required) && <span style={{ color: 'red' }}>*</span>}
            </Text>
            {renderInputType(qItem)}
          </div>,
        );
        displayIdx++;
      }
    }
    return rendered;
  };

  if (loading)
    return <div style={{ textAlign: 'center', padding: '50px' }}></div>;

  return (
    <div style={{ padding: '20px', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header Title */}
        <Card
          style={{
            marginBottom: 20,
            borderRadius: '4px',
            border: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BackButton />
            <Title
              level={4}
              style={{ textAlign: 'center', margin: '0 auto', color: '#333' }}
            >
              {survey?.title || 'Survey Details'}
            </Title>
          </div>
        </Card>

        <Card
          style={{
            marginBottom: 20,
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
            padding: '20px',
          }}
        >
          {renderQuestions()}
        </Card>

        {/* Footer Actions */}
        <Card
          style={{
            marginTop: 20,
            borderRadius: '4px',
            border: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Button
                size="middle"
                onClick={() => navigate(-1)}
                style={{ width: 120 }}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Space size="middle">
                <Button
                  size="middle"
                  loading={submittingAction === 'Rejected'}
                  disabled={submittingAction === 'Approved'}
                  danger
                  ghost
                  onClick={() => {
                    Modal.confirm({
                      title: 'Confirm Action',
                      content: 'Are you sure you want to reject?',
                      okText: 'Yes',
                      okType: 'danger',
                      cancelText: 'No',
                      onOk: () => handleApprovalAction('Rejected'),
                    });
                  }}
                  style={{
                    width: 120,
                  }}
                >
                  Reject
                </Button>
                <Button
                  type="primary"
                  size="middle"
                  loading={submittingAction === 'Approved'}
                  disabled={submittingAction === 'Rejected'}
                  onClick={() => handleApprovalAction('Approved')}
                  style={{
                    width: 120,
                  }}
                >
                  Approve
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default ApprovalsQuestions;
